import Modal from '@src/components/Modal';
import {
  computeFaceFrequencies,
  getFirstDetectionForFace,
} from '@src/services/framing/faceFrequency';
import { suggestSpeakersFromVoiceTiming } from '@src/services/framing/voiceTimingSuggestions';
import { getSpeakerColor } from '@src/services/SpeakerManagementService';
import { LogManager } from '@src/utility/Logger';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import FaceThumbnail from './FaceThumbnail';

import type { SpeakerLabelToIdMappings } from '@src/services/framing/speakerRanking';
import type {
  FaceDetection,
  FaceTracks,
  SpeakerIdentifications,
} from '@src/services/framing/types';
import type { DatoSpeaker } from '@src/utility/speakerUtils';

const logger = LogManager.getLogger('feature:FaceMappingModal');

const UNMAP_SENTINEL = '__unmap__';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  storyId: string;
  videoUrl: string | undefined;
  faceDetections: FaceDetection[];
  faceTracks: FaceTracks;
  speakerIdentifications: SpeakerIdentifications;
  /** face_label → transcript speaker_label, matched by who talks on screen. */
  speakerFaceMapping: Record<string, string>;
  voiceMappings: SpeakerLabelToIdMappings;
  speakers: DatoSpeaker[];
  hasSpeakerWithHeadshot: boolean;
  identifying: boolean;
  detecting: boolean;
  savingOverride: boolean;
  onRunIdentification: (forceRerun: boolean) => Promise<void>;
  onSaveOverride: (map: Record<string, string>) => Promise<boolean>;
};

const FaceMappingModal = (props: Props) => {
  const {
    isOpen,
    onClose,
    storyId,
    videoUrl,
    faceDetections,
    faceTracks,
    speakerIdentifications,
    speakerFaceMapping,
    voiceMappings,
    speakers,
    hasSpeakerWithHeadshot,
    identifying,
    detecting,
    savingOverride,
    onRunIdentification,
    onSaveOverride,
  } = props;

  const [pendingMap, setPendingMap] = useState<
    Record<string, string | typeof UNMAP_SENTINEL>
  >({});

  const speakerById = useMemo(() => {
    const map: Record<string, DatoSpeaker> = {};
    for (const s of speakers) map[s.id] = s;
    return map;
  }, [speakers]);

  const orderedFaces = useMemo(
    () => computeFaceFrequencies(faceTracks),
    [faceTracks],
  );

  const visibleFaces = useMemo(
    () => orderedFaces.filter(({ count }) => count > 1),
    [orderedFaces],
  );

  // Faces whose suggestion the user cleared. Kept until the modal closes, so a
  // save's refetch doesn't bring the suggestion back.
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(
    () => new Set(),
  );

  // Pre-fill faces the headshot match left unmapped with the speaker who was
  // talking while they were on screen. Shown as unsaved edits until saved.
  const voiceTimingSuggestions = useMemo(
    () =>
      suggestSpeakersFromVoiceTiming(
        visibleFaces
          .map(f => f.faceLabel)
          .filter(faceLabel => !dismissedSuggestions.has(faceLabel)),
        speakerIdentifications,
        speakerFaceMapping,
        voiceMappings,
        new Set(Object.keys(speakerById)),
      ),
    [
      visibleFaces,
      dismissedSuggestions,
      speakerIdentifications,
      speakerFaceMapping,
      voiceMappings,
      speakerById,
    ],
  );

  const resolveAssignedSpeakerId = (
    faceLabel: string,
  ): string | typeof UNMAP_SENTINEL | undefined => {
    const pending = pendingMap[faceLabel];
    if (pending !== undefined) return pending;
    return (
      speakerIdentifications[faceLabel] ?? voiceTimingSuggestions[faceLabel]
    );
  };

  const isSuggested = (faceLabel: string): boolean =>
    pendingMap[faceLabel] === undefined &&
    voiceTimingSuggestions[faceLabel] !== undefined;

  const hasPendingEdits =
    Object.keys(pendingMap).length > 0 ||
    Object.keys(voiceTimingSuggestions).length > 0;

  const close = () => {
    setPendingMap({});
    setDismissedSuggestions(new Set());
    onClose();
  };

  const onSelectSpeaker = (faceLabel: string, value: string) => {
    const currentServer = speakerIdentifications[faceLabel];
    const suggestion = voiceTimingSuggestions[faceLabel];
    const clearing = value === UNMAP_SENTINEL || value === '';
    const dismissing = clearing && !currentServer && suggestion !== undefined;
    if (dismissing) {
      // Nothing saved to remove; just stop suggesting.
      setDismissedSuggestions(prev => new Set(prev).add(faceLabel));
    }
    // What the row shows with no pending edit.
    const baseline = currentServer ?? (dismissing ? undefined : suggestion);
    setPendingMap(prev => {
      const next = { ...prev };
      if (value === UNMAP_SENTINEL) {
        // Pending unmap diverges from server only if there's currently an
        // identification (which we'd remove from the override map).
        if (currentServer) {
          next[faceLabel] = UNMAP_SENTINEL;
        } else {
          delete next[faceLabel];
        }
      } else if (value === (baseline ?? '')) {
        delete next[faceLabel];
      } else {
        next[faceLabel] = value;
      }
      return next;
    });
  };

  const onSave = async () => {
    const finalMap: Record<string, string> = {
      ...voiceTimingSuggestions,
      ...speakerIdentifications,
    };
    for (const [faceLabel, value] of Object.entries(pendingMap)) {
      if (value === UNMAP_SENTINEL || value === '') {
        delete finalMap[faceLabel];
      } else {
        finalMap[faceLabel] = value;
      }
    }
    logger.info(
      `Saving speaker identifications override (${
        Object.keys(finalMap).length
      } entries)`,
    );
    const ok = await onSaveOverride(finalMap);
    if (ok) {
      setPendingMap({});
    }
  };

  const onRerun = async (force: boolean) => {
    if (
      force &&
      !window.confirm(
        'Re-run the full detection pipeline? This is expensive and will discard manual remaps.',
      )
    ) {
      return;
    }
    await onRunIdentification(force);
  };

  const speakerName = (id: string | undefined): string => {
    if (!id) return 'Unmapped';
    return speakerById[id]?.name ?? id;
  };

  const swatchColor = (
    speakerId: string | typeof UNMAP_SENTINEL | undefined,
  ): string => {
    if (!speakerId || speakerId === UNMAP_SENTINEL) return '#A9A9A9';
    return speakerId ? getSpeakerColor(speakerId, 0) : '#A9A9A9';
  };

  const status: string | null = detecting
    ? 'Re-running full detection…'
    : identifying
      ? 'Re-running identification…'
      : savingOverride
        ? 'Saving remaps…'
        : null;

  const runDisabled = !hasSpeakerWithHeadshot || identifying || detecting;
  const runDisabledReason = !hasSpeakerWithHeadshot
    ? 'Add at least one headshot to a speaker first'
    : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      customWidth="720px"
      customMinHeight="420px"
    >
      <Header>Face Mapping</Header>

      <ActionBar>
        <ActionButton
          onClick={() => onRerun(false)}
          disabled={runDisabled}
          title={runDisabledReason}
        >
          Re-run Identification
        </ActionButton>
        <ActionButton
          onClick={() => onRerun(true)}
          disabled={runDisabled}
          title={runDisabledReason}
        >
          Force Re-run All Detections
        </ActionButton>
        {status && <StatusText>{status}</StatusText>}
      </ActionBar>

      <Table>
        <TableHeader>
          <ColSwatch />
          <ColThumb>Face</ColThumb>
          <ColLabel>Label</ColLabel>
          <ColSpeaker>Speaker</ColSpeaker>
        </TableHeader>
        <TableBody>
          {orderedFaces.length === 0 && <EmptyRow>No faces detected.</EmptyRow>}
          {visibleFaces.map(({ faceLabel, count }) => {
            const first = getFirstDetectionForFace(faceDetections, faceLabel);
            const assignedSpeakerId = resolveAssignedSpeakerId(faceLabel);
            const selectValue =
              assignedSpeakerId === UNMAP_SENTINEL
                ? UNMAP_SENTINEL
                : (assignedSpeakerId ?? '');
            const suggested = isSuggested(faceLabel);
            const isEdited = pendingMap[faceLabel] !== undefined || suggested;
            return (
              <Row key={faceLabel} $edited={isEdited}>
                <ColSwatch>
                  <Swatch
                    style={{
                      backgroundColor: swatchColor(assignedSpeakerId),
                    }}
                  />
                </ColSwatch>
                <ColThumb>
                  {first ? (
                    <FaceThumbnail
                      videoUrl={videoUrl}
                      time={first.time}
                      box={first.box}
                      cacheKey={`${storyId}-${faceLabel}`}
                      label={faceLabel}
                    />
                  ) : (
                    <NoFace>—</NoFace>
                  )}
                </ColThumb>
                <ColLabel>
                  <FaceLabel>{faceLabel}</FaceLabel>
                  <FaceCount>{count} frames</FaceCount>
                  {suggested && (
                    <SuggestedHint>Suggested from voice timing</SuggestedHint>
                  )}
                </ColLabel>
                <ColSpeaker>
                  <Select
                    value={selectValue}
                    onChange={e => onSelectSpeaker(faceLabel, e.target.value)}
                  >
                    <option value="">— Choose speaker —</option>
                    {speakers.map(s => (
                      <option key={s.id} value={s.id}>
                        {speakerName(s.id)}
                      </option>
                    ))}
                    <option value={UNMAP_SENTINEL}>— Unmap —</option>
                  </Select>
                </ColSpeaker>
              </Row>
            );
          })}
        </TableBody>
      </Table>

      <Footer>
        <FooterNote>
          Tip: To correct a wrong face↔speaker link without saving an override,
          add the missing headshot to the speaker in the Speakers panel and
          click <em>Re-run Identification</em>.
        </FooterNote>
        <FooterButtons>
          <SecondaryButton onClick={close} disabled={savingOverride}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={onSave}
            disabled={!hasPendingEdits || savingOverride}
          >
            Save Remaps
          </PrimaryButton>
        </FooterButtons>
      </Footer>
    </Modal>
  );
};

export default FaceMappingModal;

const Header = styled.h2`
  margin: 0 0 16px 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-semi-bold);
  text-align: left;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--foreground);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusText = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
  font-style: italic;
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 16px 88px 1fr 220px;
  gap: 12px;
  padding: 8px 12px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  text-align: left;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div<{ $edited: boolean }>`
  display: grid;
  grid-template-columns: 16px 88px 1fr 220px;
  gap: 12px;
  padding: 8px 12px;
  align-items: center;
  border-bottom: 1px solid var(--border);
  background: ${({ $edited }) =>
    $edited ? 'var(--surface-elevated)' : 'transparent'};

  &:last-child {
    border-bottom: none;
  }
`;

const EmptyRow = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
`;

const ColSwatch = styled.div`
  display: flex;
  align-items: center;
`;

const ColThumb = styled.div`
  display: flex;
  align-items: center;
`;

const ColLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
`;

const ColSpeaker = styled.div``;

const Swatch = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
`;

const FaceLabel = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const FaceCount = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const SuggestedHint = styled.span`
  font-size: var(--text-xs);
  color: var(--accent);
`;

const Select = styled.select`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--background);
  color: var(--foreground);
  font-size: var(--text-xs);
  cursor: pointer;
`;

const NoFace = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 6px;
  background: var(--surface);
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: var(--text-muted);
`;

const Footer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterNote = styled.div`
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-align: left;
  line-height: 1.4;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SecondaryButton = styled(ActionButton)``;

const PrimaryButton = styled(ActionButton)`
  background: var(--primary);
  color: var(--primary-foreground);
  border-color: var(--primary);

  &:hover:not(:disabled) {
    opacity: 0.92;
    border-color: var(--primary);
  }
`;
